/** Hiring-demand collection — the shapes the board reads and writes. */

export type MonthBand = 'past' | 'plan' | 'lookahead';

export interface DemandMonth {
  key: string; // YYYY-MM
  label: string; // "Sep"
  year: number;
  band: MonthBand;
  /** Collapsed by default: everything before (current month − 1). */
  past: boolean;
}

/** The four buckets an HDB status is normalised into. */
export type HdbStatus = 'hired' | 'open' | 'hold' | 'backlog';

/** Why the role exists — what drives backfill planning. */
export type HireReason = 'backfill' | 'new' | 'unknown';

/** A draft's own lifecycle, entirely separate from an HDB status. */
export type DraftState = 'draft' | 'approving' | 'synced';

/**
 * Where a role's team came from. The HDB has no team column, so the board's
 * third level is derived server-side (taDemandTeams.js) and this says how it was
 * arrived at: a lead moved it, the row already named one, the job-family map
 * placed it, or nothing could.
 */
export type TeamSource = 'override' | 'org-design' | 'row' | 'family' | 'title' | 'none';

/** An approved role. The HDB owns every field — the board never edits these. */
/**
 * What became of a filled seat.
 *
 * `unfilled` — nobody hired yet. `landed` — hired and already working here, so
 * the person shows on this row and NOT in the current team. `incoming` — signed,
 * start date still ahead; the row carries their details but they are not on the
 * team yet. `undated` — hired with no start date recorded at all, which is a gap
 * to fix rather than a state to display. `unmatched` — should be here by now and
 * the roster has nobody by that name (they may have left). `ambiguous` — the
 * name belongs to two colleagues, so nothing is merged until a human says which.
 * `superseded` — an earlier hire of somebody who has since been hired again.
 * `unlinked` — the people database could not be read at all, so no claim about
 * whether this hire works here is possible. NOT the same as `unmatched`, which
 * is a real answer from a roster that loaded.
 */
export type HireState =
  | 'unfilled'
  | 'landed'
  | 'incoming'
  | 'undated'
  | 'unmatched'
  | 'ambiguous'
  | 'superseded'
  | 'unlinked';

/** The colleague on the other side of a filled role, where one was found. */
export interface RoleHire {
  name: string;
  started: boolean;
  startDate: string | null;
  offerAcceptedMonth: string | null;
  /** 'linked' or 'separate' when a human decided this, null when matching did. */
  decided: 'linked' | 'separate' | null;
  person: HirePerson | null;
  /** Set when they sit in a different unit than the one that hired them. */
  movedUnit?: string | null;
}

export interface HirePerson {
  id: string;
  name: string;
  position: string;
  level: string;
  fte: number;
  department: string;
  unit: string;
  team: string;
  external: boolean;
  onLeave: boolean;
  contractEnd: string | null;
}

/**
 * A hired role the board cannot place in time: the HDB records no actual start
 * date, so whether this person is already at work is unanswerable. A data gap
 * with a name and an owner rather than a decision for anyone to take.
 */
export interface HireFixup {
  roleId: string;
  kind: 'no-start-date';
  name: string;
  title: string;
  department: string;
  unit: string;
  offerAcceptedMonth: string | null;
  plannedMonth: string | null;
}

/** A hire the join could not settle on its own. */
export interface HireReviewItem {
  roleId: string;
  kind: 'ambiguous' | 'unmatched';
  name: string;
  title: string;
  unit: string;
  startDate?: string | null;
  candidates?: { id: string; name: string; unit: string; team: string; position: string }[];
}

export interface HdbRole {
  id: string;
  source: 'hdb';
  recordId: string;
  title: string;
  department: string;
  unit: string;
  team: string;
  level: string;
  hiringManager: string;
  status: HdbStatus;
  /** What the HDB column literally said, so an unmapped value stays visible. */
  statusRaw: string;
  reason: HireReason;
  reasonRaw: string;
  /** Where the row sits: actual start, else the adjusted plan, else the plan. */
  month: string | null;
  /** The ORIGINAL commitment. The slip is always measured against this. */
  plannedMonth: string | null;
  /** The revised expectation, when the plan has already been moved once. */
  adjustedMonth: string | null;
  /** When somebody really began. Null while the seat is still empty. */
  actualMonth: string | null;
  /** The month the hire was WON. What "hired" counts. */
  offerAcceptedMonth: string | null;
  /** No date anywhere yet — real demand with no place on the timeline. */
  undated: boolean;
  /** Job-architecture family, distinct from the position title. */
  roleFamily: string;
  /** The HDB's own stable key for a seat — what lines a frozen baseline up. */
  positionId: string;
  recruiter: string;
  /** Who this backfill replaces — the HDB links the leaver directly. */
  backfillFor: string;
  costCategory: string;
  count: number;
  fte: number;
  viaPlanner: boolean;
  /** The start DAY, for "has this person actually begun?" — see taDemandHires. */
  actualStartDate?: string | null;
  /** Who got the job. Empty until the role is filled. */
  hiredName?: string;
  /**
   * The colleague this seat became. OPTIONAL because a role can reach the UI
   * without having been through the join — a frozen baseline shaped before this
   * existed, for one — and a board that renders such a row as blank is better
   * than one that throws.
   */
  hire?: RoleHire | null;
  hireState?: HireState;
  teamSource?: TeamSource;
  movedWithTeam?: string;
  placedBy?: string;
  placedAt?: string;
}

/** A role that exists only here until someone approves it. */
export interface DemandDraft {
  id: string;
  source: 'planner';
  state: DraftState;
  title: string;
  department: string;
  unit: string;
  team: string;
  level: string;
  reason: HireReason;
  /** The people-database record this backfills, when raised from the roster. */
  employeeId?: string;
  backfillFor?: string;
  month: string;
  plannedMonth: string;
  adjustedMonth: string | null;
  actualMonth: string | null;
  offerAcceptedMonth?: string | null;
  undated?: boolean;
  count: number;
  fte: number;
  note?: string;
  createdBy?: string;
  updatedBy?: string;
  updatedAt?: string;
  recordId?: string;
  lastApprovalError?: string;
  teamSource?: TeamSource;
}

/**
 * A seat that already exists. Read from the people database (FINN's Personio
 * mirror) so a team with no open roles is still visible on the board — which is
 * the only way a backfill can be raised against a seat the HDB never had.
 *
 * Deliberately carries no email, no hire date and nothing performance-related:
 * this board has no RBAC gate, so everything here is company-visible.
 */
export interface RosterMember {
  id: string;
  name: string;
  department: string;
  unit: string;
  team: string;
  position: string;
  level: string;
  fte: number;
  /** When a fixed-term contract runs out, `YYYY-MM-DD`, if the base records one. */
  contractEnd?: string;
  /** Whether the FTE came from a record or from the org design filling a gap. */
  fteSource?: 'record' | 'default';
  fteFrom?: 'org-design';
  /** Employed through an EOR (Workmotion). Shown, marked, NOT internal headcount. */
  external?: boolean;
  /** Employed but not at work — parental leave. Shown, marked, out of the FTE. */
  onLeave?: boolean;
  /** What the people database literally said, so a wrong call is traceable. */
  standing?: string;
  /** Set when a restructure moved this row's whole team to another unit. */
  movedWithTeam?: string;
  placedBy?: string;
  placedAt?: string;
  teamSource?: TeamSource;
  /**
   * The role row this person is shown on. Set when they were hired through a
   * role inside the board's window, and it means DO NOT DRAW THEM HERE — their
   * seat is on the timeline. A person appears exactly once on the board.
   */
  onRoleRow?: string;
  /**
   * EVERY role this person was matched to, latest first — not only the one they
   * sit on. A narrowed window can drop the latest of two hires, and without the
   * rest the person would return to the current team while an earlier role of
   * theirs still had a row: one colleague drawn twice, FTE counted twice.
   */
  onRoleLinks?: string[];
}

export interface OrgUnit {
  name: string;
  owner: string | null;
  teams: string[];
  /** Other spellings that resolve here — the HDB names units its own way. */
  aliases?: string[];
  /** Which sources know this unit: people-db, hdb, org-chart. */
  sources?: string[];
  /** People in it today, from the people database. */
  headcount?: number;
}

/** A name and how many rows carry it. */
export interface NameCount {
  name: string;
  count: number;
}

/**
 * What the two source systems actually contain, and where they disagree.
 * Rendered on the board so "is this the right data?" is answerable without
 * querying Airtable.
 */
export interface DemandDiagnostics {
  people: {
    available: boolean;
    employees: number;
    /** Which column decided "still on the team", and what it excluded. */
    activeColumn: string | null;
    statusColumn: string | null;
    statusCounts: NameCount[];
    /** What the headcount SELECT actually contains, value by value. */
    headcountCounts?: NameCount[];
    excluded: number;
    external?: number;
    onLeave?: number;
    /** Left off because their fixed-term contract has already run out. */
    contractEnded?: number;
    /** Rows the headcount column does not cover; they fall through to status. */
    missingHeadcount?: number;
    /** How often the two columns a lead reads are simply empty upstream. */
    missingLevel?: number;
    missingFte?: number;
    levelColumn?: string | null;
    fteColumn?: string | null;
    /** Every column the people database actually has. */
    columns?: string[];
    /** Headcount values that named no live state — the list to read when it looks wrong. */
    unknownStanding?: NameCount[];
    /** True when NONE were recognised: the column is not the one we think it is. */
    vocabularyFailed?: boolean;
    departments: NameCount[];
    units: NameCount[];
    missingUnit: number;
    missingDepartment: number;
  };
  hdb: { roles: number; departments: NameCount[]; units: NameCount[] };
  /** Job titles no team could claim, per unit — what a mapping needs to cover. */
  unplacedTitles?: { unit: string; people: number; titles: NameCount[] }[];
  /** How each colleague got their team: org design, job title, or nothing. */
  teamSources?: NameCount[];
  /** Where the org design workbook and the people database disagree. */
  orgDesign?: { placed: number; onlyInPeopleDb: string[]; onlyInPeopleDbCount: number };
  onlyInHdb: string[];
  onlyInPeopleDb: string[];
}

export interface OrgDepartment {
  name: string;
  owner: string | null;
  units: OrgUnit[];
  aliases?: string[];
}

/** A unit lead's own sign-off, separate from the department's final one. */
export interface UnitMeta {
  department: string;
  unit: string;
  submittedBy?: string;
  submittedAt?: string;
  submitCount?: number;
}

export interface SectionMeta {
  lastEditBy?: string;
  lastEditAt?: string;
  submittedBy?: string;
  submittedAt?: string;
  submitCount?: number;
}

/**
 * A frozen copy of the Hiring Database. Airtable keeps no field-level history
 * through its API, so a baseline nobody wrote down does not exist — see
 * proxy-server/lib/taDemandSnapshot.js. Metadata only here; the rows stay on the
 * server until something asks for them.
 */
export interface DemandSnapshot {
  id: string;
  period: string;
  label: string;
  kind: 'baseline' | 'snapshot';
  source: string;
  takenBy: string;
  takenAt: string;
  note?: string;
  rowCount: number;
  chunks: number;
}

/**
 * Where something sits after a restructure, when it no longer sits where its
 * source system says. A field left out means unchanged.
 */
export interface Placement {
  department?: string;
  unit?: string;
  team?: string;
  by?: string;
  at?: string;
}

/** What a move addresses: one role, one person, or a whole team. */
export type PlacementKind = 'role' | 'person' | 'team';

export interface DemandBoard {
  period: string;
  months: DemandMonth[];
  currentMonth: string;
  /** First month of the round's trimester — what "hired this trimester" counts from. */
  trimesterStart: string;
  demandStatuses: HdbStatus[];
  structure: OrgDepartment[];
  departments: string[];
  hdb: HdbRole[];
  /** Approved then dropped. Shown collapsed under their unit, counted in nothing. */
  cancelled?: HdbRole[];
  /** Hires nobody could safely match to a colleague — a human decides these. */
  hireReview?: HireReviewItem[];
  /** Hired rows with no actual start date — a gap in the HDB, with owners. */
  hireFixups?: HireFixup[];
  hireStats?: Record<string, number>;
  hireLinks?: Record<string, string | null>;
  hdbAvailable: boolean;
  hdbUnavailableReason: string | null;
  hdbUnmappedFields: string[];
  drafts: DemandDraft[];
  roster: RosterMember[];
  rosterAvailable: boolean;
  rosterUnavailableReason: string | null;
  diagnostics: DemandDiagnostics;
  sections: Record<string, SectionMeta>;
  /** Keyed `${department}|${unit}`. */
  units: Record<string, UnitMeta>;
  extraTeams: Record<string, string[]>;
  /** Keyed `role:id`, `person:id` or `team:Dept|Unit|Name`. */
  placements?: Record<string, Placement>;
  /** Every baseline frozen for this period, newest first. */
  snapshots?: DemandSnapshot[];
  canApprove: boolean;
  viewer: string;
}

/** What the composer sends; the server assigns the id when it is omitted. */
export interface DraftInput {
  department: string;
  unit: string;
  team: string;
  title: string;
  level: string;
  reason: HireReason;
  month: string;
  count: number;
  fte: number;
  employeeId?: string;
  backfillFor?: string;
}

export type BoardRole = HdbRole | DemandDraft;

export const isDraft = (r: BoardRole): r is DemandDraft => r.source === 'planner';

/** A draft is editable only while it has not crossed over into the HDB. */
export const isEditable = (r: BoardRole): boolean => isDraft(r) && r.state === 'draft';
